// Motion for the home page: the Quick History demo in the hero, the demo
// chapters, the billing toggle and scroll reveals. Everything stops when the
// page is replaced, hidden, or the visitor prefers reduced motion.

const heroScenarios = ["invoice", "logo", "meet"];
const chapterMs = 7000;
let stopCurrent = () => {};

const sleep = (ms, signal) => new Promise((resolve, reject) => {
  const timer = setTimeout(resolve, ms);
  signal.addEventListener("abort", () => { clearTimeout(timer); reject(signal.reason); }, { once: true });
});

function reducedMotion() {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

// Resolves once the element is on screen and the tab is visible.
function whenVisible(element, signal) {
  return new Promise((resolve, reject) => {
    let onScreen = false;
    const check = () => { if (onScreen && document.visibilityState === "visible") { cleanup(); resolve(); } };
    const observer = new IntersectionObserver((entries) => { onScreen = entries.some((entry) => entry.isIntersecting); check(); });
    const onVisibility = () => check();
    const cleanup = () => { observer.disconnect(); document.removeEventListener("visibilitychange", onVisibility); };
    observer.observe(element);
    document.addEventListener("visibilitychange", onVisibility);
    signal.addEventListener("abort", () => { cleanup(); reject(signal.reason); }, { once: true });
  });
}

async function runHero(root, signal) {
  const panel = root.querySelector(".lp-quick");
  const keys = root.querySelector(".lp-keys--hero");
  const typed = root.querySelector("[data-typed]");
  const count = root.querySelector("[data-count]");
  const list = root.querySelector("[data-clip-list]");
  const pasted = root.querySelector("[data-pasted]");
  if (!panel || !typed || !list) return;
  const rows = () => [...list.children];
  const filter = (query) => {
    let shown = 0;
    for (const row of rows()) {
      const haystack = `${row.dataset.tags} ${row.textContent}`.toLowerCase();
      const match = !query || haystack.includes(query);
      row.classList.toggle("is-filtered", !match);
      row.classList.remove("is-selected", "is-sent");
      if (match) shown += 1;
    }
    count.textContent = query ? `${shown} match${shown === 1 ? "" : "es"}` : `${rows().length} clips`;
  };
  panel.classList.add("is-open");
  for (let round = 0; ; round += 1) {
    await whenVisible(panel, signal);
    const query = heroScenarios[round % heroScenarios.length];
    keys?.classList.add("is-pressed");
    panel.classList.add("is-summoned");
    await sleep(380, signal);
    keys?.classList.remove("is-pressed");
    for (let index = 1; index <= query.length; index += 1) {
      typed.textContent = query.slice(0, index);
      filter(typed.textContent);
      await sleep(110, signal);
    }
    const target = rows().find((row) => !row.classList.contains("is-filtered"));
    await sleep(500, signal);
    target?.classList.add("is-selected");
    await sleep(700, signal);
    target?.classList.add("is-sent");
    pasted.classList.add("is-shown");
    await sleep(1300, signal);
    pasted.classList.remove("is-shown");
    panel.classList.remove("is-summoned");
    typed.textContent = "";
    filter("");
    // A fresh copy arrives at the top of the list.
    const last = list.lastElementChild;
    if (last) {
      last.classList.add("is-new");
      list.prepend(last);
      last.querySelector("small").textContent = `${last.querySelector("small").textContent.split(" · ")[0]} · now`;
    }
    await sleep(1600, signal);
    last?.classList.remove("is-new");
  }
}

const formatTime = (ms) => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

// Real recordings play as video. Illustrated scenes run on CSS animations,
// which the Web Animations API lets us pause and seek like a video.
function videoClip(video) {
  video.controls = false;
  video.loop = false;
  return {
    get duration() { return Number.isFinite(video.duration) && video.duration > 0 ? video.duration * 1000 : chapterMs; },
    get time() { return video.currentTime * 1000; },
    set time(ms) { video.currentTime = Math.max(0, ms) / 1000; },
    get ended() { return video.ended; },
    play() { video.play?.().catch(() => {}); },
    pause() { video.pause?.(); },
    tick() {},
  };
}

function sceneClip(slide) {
  let time = 0;
  const animations = () => slide.getAnimations?.({ subtree: true }).filter((animation) => animation.effect?.target !== slide) ?? [];
  const seek = () => { for (const animation of animations()) { animation.pause(); animation.currentTime = time; } };
  return {
    duration: chapterMs,
    get time() { return time; },
    set time(ms) { time = Math.min(chapterMs, Math.max(0, ms)); seek(); },
    get ended() { return time >= chapterMs; },
    play() {},
    pause() { seek(); },
    tick(delta) { time = Math.min(chapterMs, time + delta); seek(); },
  };
}

async function runChapters(root, signal) {
  const buttons = [...root.querySelectorAll("[data-chapter]")];
  const stage = root.querySelector("[data-demo-stage]");
  const player = root.querySelector("[data-player]");
  if (!buttons.length || !stage || !player) return;
  const toggle = player.querySelector("[data-player-toggle]");
  const seekBar = player.querySelector("[data-player-seek]");
  const clock = player.querySelector("[data-player-time]");
  let index = 0;
  let clip = null;
  let userPaused = reducedMotion();
  let onScreen = false;
  let seeking = false;
  const playing = () => !userPaused && onScreen && document.visibilityState === "visible";

  const paint = () => {
    const fraction = clip ? Math.min(1, clip.time / clip.duration) : 0;
    if (!seeking) seekBar.value = String(Math.round(fraction * 1000));
    player.style.setProperty("--lp-seek", `${fraction * 100}%`);
    seekBar.setAttribute("aria-valuetext", `${formatTime(clip?.time ?? 0)} of ${formatTime(clip?.duration ?? 0)}`);
    clock.textContent = `${formatTime(clip?.time ?? 0)} / ${formatTime(clip?.duration ?? 0)}`;
    buttons[index].querySelector(".lp-chapter-progress i")?.style.setProperty("width", `${fraction * 100}%`);
    const label = userPaused ? "Play" : "Pause";
    toggle.setAttribute("aria-label", label);
    toggle.title = label;
    player.classList.toggle("is-paused", userPaused);
  };
  const sync = () => { if (playing()) clip?.play(); else clip?.pause(); paint(); };

  const show = (next) => {
    clip?.pause();
    index = (next + buttons.length) % buttons.length;
    buttons.forEach((button, at) => {
      const active = at === index;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
      button.querySelector(".lp-chapter-progress i")?.style.setProperty("width", "0%");
    });
    for (const slide of stage.querySelectorAll("[data-stage]")) {
      const active = slide.dataset.stage === buttons[index].dataset.chapter;
      slide.hidden = !active;
      slide.classList.toggle("is-active", active);
      if (!active) continue;
      const video = slide.querySelector("video");
      clip = video ? videoClip(video) : sceneClip(slide);
      clip.time = 0;
      video?.addEventListener("loadedmetadata", paint, { once: true, signal });
    }
    sync();
  };

  buttons.forEach((button, at) => button.addEventListener("click", () => { userPaused = false; show(at); }, { signal }));
  toggle.addEventListener("click", () => {
    userPaused = !userPaused;
    if (!userPaused && clip?.ended) clip.time = 0;
    sync();
  }, { signal });
  player.querySelector("[data-player-back]").addEventListener("click", () => {
    // Early in a chapter, Back steps to the previous one, like a playlist.
    if (clip && clip.time < 1000) show(index - 1);
    else if (clip) { clip.time -= 5000; paint(); }
  }, { signal });
  seekBar.addEventListener("input", () => {
    seeking = true;
    if (clip) clip.time = (Number(seekBar.value) / 1000) * clip.duration;
    paint();
  }, { signal });
  seekBar.addEventListener("change", () => { seeking = false; paint(); }, { signal });

  const observer = new IntersectionObserver((entries) => { onScreen = entries.some((entry) => entry.isIntersecting); sync(); });
  observer.observe(stage);
  document.addEventListener("visibilitychange", sync, { signal });
  signal.addEventListener("abort", () => { observer.disconnect(); clip?.pause(); }, { once: true });

  player.hidden = false;
  show(0);
  let last = performance.now();
  for (;;) {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (signal.aborted) return;
    const now = performance.now();
    const delta = now - last;
    last = now;
    if (!playing() || seeking || !clip) continue;
    clip.tick(delta);
    if (clip.ended) show(index + 1);
    else paint();
  }
}

function wireBilling(root, signal) {
  for (const plan of root.querySelectorAll("[data-billing-prices]")) {
    const card = plan.closest(".lp-plan");
    for (const button of card.querySelectorAll("[data-billing]")) {
      button.addEventListener("click", () => {
        for (const other of card.querySelectorAll("[data-billing]")) other.setAttribute("aria-pressed", String(other === button));
        for (const option of plan.querySelectorAll("[data-interval]")) option.hidden = option.dataset.interval !== button.dataset.billing;
      }, { signal });
    }
  }
}

function wireReveal(root, signal) {
  const targets = root.querySelectorAll("[data-reveal]");
  if (reducedMotion() || !("IntersectionObserver" in globalThis)) { targets.forEach((node) => node.classList.add("is-in")); return; }
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) if (entry.isIntersecting) { entry.target.classList.add("is-in"); observer.unobserve(entry.target); }
  }, { rootMargin: "0px 0px -8% 0px" });
  targets.forEach((node) => observer.observe(node));
  signal.addEventListener("abort", () => observer.disconnect(), { once: true });
}

function wireGlow(root, signal) {
  for (const tile of root.querySelectorAll(".lp-tile, .lp-plan")) {
    tile.addEventListener("pointermove", (event) => {
      const box = tile.getBoundingClientRect();
      tile.style.setProperty("--mx", `${event.clientX - box.left}px`);
      tile.style.setProperty("--my", `${event.clientY - box.top}px`);
    }, { signal });
  }
}

const sparkColors = ["#ffb02e", "#59f0ae", "#8e7cff", "#ff6a2b", "#fff3d6"];
function wireSparks(root, signal) {
  if (reducedMotion()) return;
  for (const button of root.querySelectorAll(".lp-btn--volt")) {
    button.addEventListener("pointerdown", (event) => {
      for (let index = 0; index < 14; index += 1) {
        const spark = document.createElement("i");
        const angle = (Math.PI * 2 * index) / 14 + Math.random() * 0.4;
        const distance = 40 + Math.random() * 50;
        spark.className = "lp-spark";
        spark.style.left = `${event.clientX}px`;
        spark.style.top = `${event.clientY}px`;
        spark.style.setProperty("--dx", `${Math.cos(angle) * distance}px`);
        spark.style.setProperty("--dy", `${Math.sin(angle) * distance}px`);
        spark.style.setProperty("--lp-spark-color", sparkColors[index % sparkColors.length]);
        document.body.append(spark);
        setTimeout(() => spark.remove(), 700);
      }
    }, { signal });
  }
}

async function runFills(root, signal) {
  const marks = [...root.querySelectorAll("mark[data-fill]")];
  if (!marks.length || reducedMotion()) return;
  const blanks = marks.map((mark) => mark.textContent);
  for (let filled = false; ; filled = !filled) {
    await sleep(filled ? 2600 : 1800, signal);
    marks.forEach((mark, at) => { mark.textContent = filled ? blanks[at] : mark.dataset.fill; mark.classList.toggle("is-filled", !filled); });
  }
}

export function startLandingMotion(root) {
  stopCurrent();
  const controller = new AbortController();
  const { signal } = controller;
  stopCurrent = () => controller.abort(new DOMException("landing replaced", "AbortError"));
  root.classList.add("lp-js");
  const quiet = (promise) => promise.catch(() => {});
  wireBilling(root, signal);
  wireReveal(root, signal);
  wireGlow(root, signal);
  wireSparks(root, signal);
  quiet(runChapters(root, signal));
  if (!reducedMotion()) {
    quiet(runHero(root, signal));
    quiet(runFills(root, signal));
  } else {
    root.querySelector(".lp-quick")?.classList.add("is-open");
  }
  // Stop when the landing is swapped out by the router.
  const watcher = new MutationObserver(() => { if (!root.isConnected) { stopCurrent(); watcher.disconnect(); } });
  watcher.observe(document.querySelector("#app") ?? document.body, { childList: true });
  signal.addEventListener("abort", () => watcher.disconnect(), { once: true });
  return stopCurrent;
}
